<template>
    <v-layout row>
        <v-flex xs12 class="text-xs-center">
            <!-- 📝 Updated: Attached terms-caption class for reliable color mapping -->
            <h1 class="caption terms-caption">
                By clicking <b>Sign In</b> button, you agree to <b class="mr-1">SweldoNow&copy</b>
                <br v-if="isMobile">
                <!-- Added explicit legal-link class for actionable items -->
                <span class="legal-link" @click="showPopup(doc_type.terms)">Terms & Conditions</span> and 
                <span class="legal-link" @click="showPopup(doc_type.privacy)">Privacy Policy</span>.                
            </h1>
        </v-flex>

        <v-dialog v-model="dialog" width="800">
            <legal-docs-viewer ref="refViewer" :dialog.sync="dialog"></legal-docs-viewer>
        </v-dialog>
    </v-layout>
</template>

<script>
export default {
    data() {
        return {
            dialog: false,
            doc_type: {
                privacy: 'Privacy Policy',
                terms: 'Terms & Conditions',
                disclaimer: 'Disclaimer',
                cookie: 'Cookie Policy',
                eula: 'EULA Policy',
                returnrefund: 'Return & Refund Policy'
            },
            data: [
                {
                    type: 'Privacy Policy',
                    header: "Privacy Policy",
                    effectiveDate: "February 9, 2024",
                    intro: `At SweldoNow, we are committed to protecting the privacy and security of our customers' personal information. This Privacy Policy outlines the types of information we collect, how we use and safeguard it, and the choices you have regarding your personal data when using our cloud payroll system. This Privacy Policy should be read together with SweldoNow’s Terms and Conditions and applicable Disclaimer.`,
                    details: [
                        {
                            title: "Information We Collect:",
                            description: "",
                            items: [
                                {
                                    item: "Personal Information: We collect personal information that you provide to us, including but not limited to your name, email address, contact number, employee details, salary information, and other relevant data necessary for payroll processing."
                                },
                                {
                                    item: "Usage Information: We automatically collect certain information, including device type, browser type, access times, and pictures used for clock-in/out attendance management and two-factor authentication. This data is collected to ensure system compatibility, analyze usage patterns, enhance security, and provide accurate attendance records."
                                },
                                {
                                    item: "Face Data: When using features that involve facial recognition (e.g., clock-in/out attendance or identity verification), we collect facial data through Face AI. This data is processed locally on the device or within secure servers to authenticate users and improve attendance accuracy."
                                }
                            ]
                        },
                        {
                            title: "Use of Information:",
                            description: "",
                            items: [
                                {
                                    item: "Payroll Processing: We collect and process personal information for the purpose of payroll processing and related services, including tax calculations, deductions, and generating payslips."
                                },
                                {
                                    item: "Communication: We may use your contact information to communicate with you regarding your payroll, updates, service-related announcements, and customer support inquiries."
                                },
                                {
                                    item: `System Improvement: We may analyze usage patterns and feedback to enhance our system's performance, develop new features, and improve user experience.`
                                },
                                {
                                    item: "Face Data Utilization: Face data is used solely for clock-in/out attendance management, fraud prevention, and security enhancement. This data is never shared with third parties or used for marketing purposes."
                                },
                                {
                                    item: "Legal Compliance: We may disclose personal information if required by law or in response to valid legal requests, such as subpoenas, court orders, or government regulations."
                                }
                            ]
                        },
                        {
                            title: "Data Security:",
                            description: "",
                            items: [
                                {
                                    item: "Safeguards: We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These measures include encryption, firewalls, access controls, and regular security assessments."
                                },
                                {
                                    item: "Face Data Protection: Face data collected is encrypted and stored securely, ensuring it is only accessible for its intended purpose. We do not retain face data longer than necessary to fulfill its function."
                                },
                                {
                                    item: "Data Transfer: We ensure that personal information transferred to and stored on our servers is done in a secure manner, utilizing encryption and secure protocols."
                                }
                            ]
                        },
                        {
                            title: "Data Retention:",
                            description: "We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Upon termination of your account or request for deletion, we will securely dispose of your personal information in accordance with our data retention practices.",
                            items: []
                        },
                        {
                            title: "Your Rights:",
                            description: "",
                            items: [
                                {
                                    item: "Access and Correction: You have the right to access and correct your personal information stored in our system. You can review, update, or delete your information by contacting our support team."
                                },
                                {
                                    item: "Face Data Control: You can opt out of facial recognition features by contacting support or deactivating face recognition attendance tracking directly within SweldoNow. Please refer to the Knowledge Hub for detailed steps on how to manage this setting. Note that opting out may limit certain functionalities such as clock-in/out attendance tracking."
                                },
                                {
                                    item: "Marketing Communications: You can opt out of receiving marketing communications from us by following the unsubscribe instructions provided in the communication or by contacting us directly."
                                }
                            ]
                        },
                        {
                            title: "Third-Party Services:",
                            description: "We may engage third-party service providers to perform functions on our behalf, such as payment processing or system maintenance. These providers may have access to your personal information only to the extent necessary to perform their services and are obligated to protect it in a manner consistent with this Privacy Policy.",
                            items: []
                        },
                        {
                            title: "Updates to Privacy Policy:",
                            description: `We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material updates by email or through our system's interface. Please review the Privacy Policy periodically to stay informed about how we collect, use, and protect your personal information.`,
                            items: []
                        },
                        {
                            title: "Contact Us:",
                            description: "If you have any questions, concerns, or requests regarding this Privacy Policy or the handling of your personal information, please contact us at support@sweldonow.ph",
                            items: []
                        }
                    ],
                    footer: `By using SweldoNow's cloud payroll system, you acknowledge that you have read and understood this Privacy Policy and that your use of the Service is also subject to SweldoNow’s Terms and Conditions and Disclaimer.`
                },
                {
                    type: 'Terms & Conditions',
                    header: 'Terms and Conditions',
                    effectiveDate: 'July 7, 2026',
                    intro: `These Terms and Conditions ("Terms") govern your access to and use of the SweldoNow Workforce Management System ("SweldoNow" or the "Service"), a cloud-based Software-as-a-Service (SaaS) platform provided by SweldoNow OPC. By accessing or using the Service, you acknowledge that you have read, understood, and agree to be legally bound by these Terms. These Terms should be read together with SweldoNow's Privacy Policy and Disclaimer.`,
                    details: [
                        {
                            title: 'Acceptance of Terms',
                            description: 'By accessing or using SweldoNow, you agree to comply with these Terms and all applicable laws and regulations. If you do not agree with any provision of these Terms, you must discontinue use of the Service immediately.',
                            items: []
                        },
                        {
                            title: 'Description of Service',
                            description: 'SweldoNow is a configurable cloud-based Workforce Management System designed to assist organizations in managing payroll, attendance, employee records, leave administration, reimbursement processing, workflow approvals, digital employee files, statutory reporting, and other workforce management functions. SweldoNow is a technology solutions provider that delivers configurable software tools and automation and does not provide legal, accounting, taxation, labor, or payroll consulting services.',
                            items: []
                        },
                        {
                            title: 'Configuration, Payroll Computation, and Customer Responsibilities',
                            description: 'SweldoNow provides payroll computation and workforce management functionality based on standard system features and customer-defined configurations.',
                            items: [
                                {
                                    item: 'Payroll computations are generated using the customer\'s configured payroll policies, work schedules, earnings, deductions, attendance records, leave records, statutory settings, and other payroll-related information.'
                                },
                                {
                                    item: 'The accuracy of payroll computations depends entirely upon the accuracy, completeness, and correctness of information supplied, imported, maintained, or approved by the Customer.'
                                },
                                {
                                    item: 'The Customer remains solely responsible for reviewing, validating, approving, and authorizing all payroll computations before employee payment, government filing, tax remittance, or statutory submission.'
                                },
                                {
                                    item: 'SweldoNow shall not be liable for payroll discrepancies arising from inaccurate employee information, incorrect configuration, incomplete attendance records, erroneous imports, customer-approved settings, or delayed implementation of customer-requested changes.'
                                },
                                {
                                    item: 'The Customer is solely responsible for ensuring compliance with applicable labor laws, tax regulations, government reporting requirements, company policies, employment contracts, and collective bargaining agreements.'
                                }
                            ]
                        },
                        {
                            title: 'Customer-Requested Customizations',
                            description: 'SweldoNow may, at its sole discretion, implement customer-requested customizations, reports, integrations, workflows, or computation logic.',
                            items: [
                                {
                                    item: 'All customizations are developed based solely upon customer-provided business requirements, specifications, approvals, and documented instructions.'
                                },
                                {
                                    item: 'The Customer shall perform adequate testing, validation, and User Acceptance Testing (UAT) before approving deployment into production.'
                                },
                                {
                                    item: 'Customer approval constitutes acceptance that the customization satisfies its intended business requirements.'
                                },
                                {
                                    item: 'SweldoNow shall not be responsible or liable for payroll discrepancies, financial losses, compliance issues, statutory differences, operational interruptions, or damages resulting from customer-requested customizations or customer-approved custom business logic.'
                                },
                                {
                                    item: 'SweldoNow reserves the right to decline customization requests that may compromise system integrity, security, maintainability, or regulatory compliance.'
                                }
                            ]
                        },
                        {
                            title: 'SweldoNow Face AI',
                            description: 'SweldoNow Face AI is an AI-powered attendance verification solution integrated with the Workforce Management System.',
                            items: [
                                {
                                    item: 'Face data is collected solely for attendance verification, identity validation, fraud prevention, and authorized security purposes.'
                                },
                                {
                                    item: 'Face data is securely processed and protected in accordance with applicable data privacy laws.'
                                },
                                {
                                    item: 'Customers are responsible for obtaining any legally required employee consent before enabling facial recognition features.'
                                },
                                {
                                    item: 'Certain Face AI features require a stable internet connection for optimal operation.'
                                }
                            ]
                        },
                        {
                            title: 'Registration and Account Security',
                            description: '',
                            items: [
                                {
                                    item: 'Customers must provide complete, accurate, and current registration information.'
                                },
                                {
                                    item: 'Customers are responsible for safeguarding account credentials and all activities performed under their accounts.'
                                },
                                {
                                    item: 'Customers shall immediately notify SweldoNow of any unauthorized account access or suspected security breach.'
                                }
                            ]
                        },
                        {
                            title: 'Data Privacy and Security',
                            description: 'SweldoNow implements commercially reasonable administrative, organizational, and technical safeguards designed to protect customer information. While industry-standard security practices are employed, no online system can guarantee absolute security.',
                            items: []
                        },
                        {
                            title: 'User Responsibilities',
                            description: '',
                            items: [
                                {
                                    item: 'Maintain accurate employee, payroll, attendance, leave, and statutory information.'
                                },
                                {
                                    item: 'Review and validate payroll results before approving payroll processing.'
                                },
                                {
                                    item: 'Verify imported data before use in payroll or workforce processing.'
                                },
                                {
                                    item: 'Maintain confidentiality of account credentials.'
                                },
                                {
                                    item: 'Use the Service only for lawful business purposes.'
                                },
                                {
                                    item: 'Not interfere with the operation, security, or availability of the Service.'
                                },
                                {
                                    item: 'Not attempt unauthorized access, reverse engineering, or modification of the Service.'
                                }
                            ]
                        },
                        {
                            title: 'Payment and Subscription',
                            description: '',
                            items: [
                                {
                                    item: 'Subscription fees shall be billed in accordance with the Customer\'s selected subscription plan.'
                                },
                                {
                                    item: 'Unless otherwise agreed in writing, subscription fees are non-refundable.'
                                },
                                {
                                    item: 'SweldoNow may revise pricing, features, or subscription plans upon reasonable prior notice.'
                                },
                                {
                                    item: 'Failure to pay outstanding subscription fees may result in suspension or termination of Service.'
                                }
                            ]
                        },
                        {
                            title: 'Intellectual Property Rights',
                            description: 'SweldoNow and all associated software, technology, documentation, trademarks, logos, source code, interfaces, and intellectual property remain the exclusive property of SweldoNow OPC. No ownership rights are transferred to Customers by virtue of using the Service.',
                            items: []
                        },
                        {
                            title: 'Professional Advice Disclaimer',
                            description: 'SweldoNow is a software solutions provider only. The Service does not constitute legal, accounting, taxation, labor, payroll outsourcing, financial, or human resource consulting services. Customers should seek qualified professional advice where necessary.',
                            items: []
                        },
                        {
                            title: 'Limitation of Liability',
                            description: '',
                            items: [
                                {
                                    item: 'SweldoNow shall not be liable for payroll errors resulting from inaccurate, incomplete, outdated, or incorrect information supplied by the Customer.'
                                },
                                {
                                    item: 'SweldoNow shall not be liable for losses arising from customer-approved configurations or customer-requested customizations.'
                                },
                                {
                                    item: 'SweldoNow shall not be liable for government penalties, surcharges, tax deficiencies, labor claims, compliance issues, or statutory filing errors resulting from Customer data or Customer decisions.'
                                },
                                {
                                    item: 'SweldoNow shall not be liable for indirect, incidental, consequential, punitive, exemplary, or special damages, including loss of profits, business interruption, goodwill, or data.'
                                },
                                {
                                    item: 'To the fullest extent permitted by applicable law, SweldoNow\'s total aggregate liability arising from the Service shall not exceed the subscription fees paid by the Customer during the three (3) months immediately preceding the event giving rise to the claim.'
                                }
                            ]
                        },
                        {
                            title: 'Termination',
                            description: '',
                            items: [
                                {
                                    item: 'Customers may terminate their subscription in accordance with SweldoNow\'s cancellation procedures.'
                                },
                                {
                                    item: 'SweldoNow may suspend or terminate access for violations of these Terms, unlawful activity, abuse of the Service, or non-payment.'
                                },
                                {
                                    item: 'Termination does not relieve the Customer of outstanding payment obligations incurred prior to termination.'
                                }
                            ]
                        },
                        {
                            title: 'Modifications to Terms',
                            description: 'SweldoNow reserves the right to amend these Terms at any time. Updated Terms become effective upon publication unless otherwise specified. Continued use of the Service constitutes acceptance of the revised Terms.',
                            items: []
                        },
                        {
                            title: 'Governing Law and Jurisdiction',
                            description: 'These Terms shall be governed by and construed in accordance with the laws of the Republic of the Philippines. Any dispute arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the competent courts of the Philippines.',
                            items: []
                        },
                        {
                            title: 'Disclaimer',
                            description: 'The SweldoNow Disclaimer forms an integral part of these Terms and is incorporated herein by reference. In the event of any inconsistency between these Terms and the Disclaimer, both shall be interpreted to provide the maximum protection permitted by applicable law.',
                            items: []
                        }
                    ],
                    footer: 'By accessing or using SweldoNow, you acknowledge that you have read, understood, and agreed to these Terms and Conditions. If you have any questions regarding these Terms, please contact SweldoNow Customer Support.'
                }
            ]       
        }
    },
    components: {
        'legal-docs-viewer': () => import("@/components/Common/LegalDocsViewer"),
    },
    methods: {
        showPopup(type) {
            const selectedData = this.data.filter(x=> x.type == type)[0]
            this.$refs.refViewer.setData(selectedData)
            this.dialog = true
        }
    },
    computed: {},
    created() {
    }
}
</script>
<template>
    <v-layout row>
        <v-flex xs12 class="text-xs-center">
            <!-- 📝 Updated: Attached terms-caption class for reliable color mapping -->
            <h1 class="caption terms-caption">
                By clicking <b>Sign In</b> button, you agree to <b class="mr-1">SweldoNow&copy</b>
                <br v-if="isMobile">
                <!-- Added explicit legal-link class for actionable items -->
                <span class="legal-link" @click="showPopup(doc_type.terms)">Terms & Conditions</span> and 
                <span class="legal-link" @click="showPopup(doc_type.privacy)">Privacy Policy</span>.                
            </h1>
        </v-flex>

        <v-dialog v-model="dialog" width="800">
            <legal-docs-viewer ref="refViewer" :dialog.sync="dialog"></legal-docs-viewer>
        </v-dialog>
    </v-layout>
</template>

<style scoped>
/* 1. Base legal text instruction parameters */
.terms-caption {
    color: #1565C0 !important;
    font-weight: 500 !important;
    line-height: 1.5 !important;
    opacity: 0.85;
}

/* Ensure nested bold tags render smoothly within the statement block */
.terms-caption b {
    color: #1565C0 !important;
    font-weight: 700 !important;
}

/* 2. Interactive action legal URLs style rules */
.legal-link {
    color: #1565C0 !important;
    font-weight: 600 !important;
    text-decoration: underline !important;
    cursor: pointer;
    transition: color 0.2s ease;
    display: inline-block;
}

/* Subtle, polished hover transition interaction state */
.legal-link:hover {
    color: #1565C0 !important; /* Shifts to a deeper blue state on cursor focus */
}
</style>